/**
 * What a name option offers, and whether it can be taken.
 *
 * The picker had three faults that compounded into something alarming:
 *
 *   * A card stayed tappable after it was claimed, so the same name could be
 *     "claimed" over and over.
 *   * The confirm dialog priced from the option card, which was generated
 *     BEFORE the claim. Re-tapping the name you already owned offered to
 *     charge you again — the server correctly charged nothing, but being asked
 *     to pay twice for something you own is frightening whether or not the
 *     money moves.
 *   * A name you could not afford looked identical to one you could, right up
 *     until the failure.
 *
 * So each card states its own situation plainly, and only an option that can
 * actually be taken is tappable.
 */

export const OPTION_STATES = {
  YOURS: 'yours',
  TAKEN: 'taken',
  SHORT: 'short',
  READY: 'ready',
}

/**
 * @param opt          {name, slug, cost, word_count, available, unavailable_reason}
 * @param balance      the seeker's carats
 * @param currentSlug  the slug they already hold, if any
 */
export function optionState(opt, { balance = 0, currentSlug = null, pricesVisible = true } = {}) {
  const o = opt || {}
  // Before the Community opens a name is free and shows no price (quest journey API v1 §6).
  const cost = pricesVisible ? (Number(o.cost) || 0) : 0

  // Already theirs — free to re-take, but there is nothing to take.
  if (currentSlug && o.slug === currentSlug) {
    return { state: OPTION_STATES.YOURS, cost: 0, shortfall: 0,
             label: 'Yours', disabled: true }
  }

  if (o.available === false) {
    return { state: OPTION_STATES.TAKEN, cost, shortfall: 0,
             label: o.unavailable_reason || 'Taken', disabled: true }
  }

  const shortfall = cost - (Number(balance) || 0)
  if (shortfall > 0) {
    // Say exactly how far off they are. "Not enough carats" after the fact
    // is a dead end; a number is something they can act on.
    return { state: OPTION_STATES.SHORT, cost, shortfall,
             label: `${shortfall} carats short`, disabled: true }
  }

  return { state: OPTION_STATES.READY, cost, shortfall: 0,
           label: !pricesVisible ? '' : (cost === 0 ? '' : `${cost} carats`), disabled: false }
}

/** What the confirm dialog should charge — 0 for a name already held. */
export function claimCost(opt, { currentSlug = null } = {}) {
  return optionState(opt, { balance: Infinity, currentSlug }).cost
}

/** Can this option be acted on at all? */
export function canClaim(opt, ctx) {
  return !optionState(opt, ctx).disabled
}
